import { EUiStates } from "@constants/enum";
import { showNotification } from "@easydev/Notification";
import { FileSize } from "@enums/fileSize";
import { getFullGalleryForUpdate } from "@helpers/createMedia.helper";
import { getPreviouslyUploadedUrlMap } from "@helpers/deleteFiles.helper";
import { fieldsToRemoveMap } from "@helpers/fieldsToRemove.helper";
import { getFilesForUpload } from "@helpers/filesForUpload";
import {
  getSignatureModule,
  signatureModulesMap,
} from "@helpers/signatureModule.helper";
import { ICreateMediaItem } from "@interfaces/createMediaItem";
import { IFileForUpload } from "@interfaces/Shared/file";
import { IFormValues } from "@interfaces/Shared/formValues";
import { useState } from "react";
import { generatePath, useHistory, useLocation } from "react-router-dom";
import { THookUpdate } from "types/Shared/hooks";
import useFileUpload from "./useFileUpload";

interface IUseUpdateItem {
  isModalOpen: boolean;
  isUpdating: boolean;
  handleUpdate: (
    APIUpdate: THookUpdate,
    uuid: string,
    callAfterUpdate?: () => void
  ) => void;
  handleIsModalOpen: () => void;
  handleSaveForm: (formValues: any, mapper: any) => void;
  totalFiles: number;
  totalSize: number;
  uploadedFiles: number;
  uploadedSize: number;
  isProgressOpen: boolean;
}

const useUpdateItem = (): IUseUpdateItem => {
  const { pathname } = useLocation();
  const history = useHistory();
  const [isUpdating, setIsUpdating] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [formValues, setFormValues] = useState<IFormValues>({});
  const [filesForUpload, setFilesForUpload] = useState<IFileForUpload[]>([]);
  const [isGalleryFilesUpload, setIsGalleryFilesUpload] = useState(false);
  const [isProgressOpen, setIsProgressOpen] = useState(false);
  const hasGalleryItemsForDelete =
    Array.isArray(formValues.galleryFilesForDelete) &&
    formValues.galleryFilesForDelete.length > 0;

  const signatureModule = getSignatureModule(pathname);

  // TODO: check how to differientiate size limit for images and videos
  const {
    getFilesUrls,
    deleteFileFromStorage,
    totalFiles,
    totalSize,
    uploadedFiles,
    uploadedSize,
    handleResetFileUpload,
  } = useFileUpload(FileSize.Max1000Mb);

  const handleIsModalOpen = () => {
    setIsModalOpen(!isModalOpen);
  };

  const handleSaveForm = (formValues: any, mapper: any) => {
    if (formValues.gallery && typeof formValues.gallery !== "string") {
      setIsGalleryFilesUpload(true);
    }

    handleIsModalOpen();
    const formattedValues = mapper(formValues);

    setFormValues(formattedValues);
    setFilesForUpload(getFilesForUpload(formValues));
  };

  const handleUpdate = async (
    APIUpdate: THookUpdate,
    uuid: string,
    callAfterUpdate?: () => void
  ) => {
    setIsUpdating(true);
    handleIsModalOpen();

    // Takes fieldsToRemove or galleryFilesForDelete and deletes them from storage, if there are any
    const checkedFormValues = hasGalleryItemsForDelete
      ? await handleDeleteMultipleFiles(formValues, uuid)
      : await handleDeleteSingleFile(formValues, uuid);

    if (filesForUpload.length > 0) {
      setIsProgressOpen(true);
    }

    // If there are files for upload, upload them and get their urls
    // If not, use checkedFormValues directly
    const formValuesForUpdate =
      filesForUpload.length > 0
        ? await getFilesUrls(
            uuid,
            signatureModulesMap[signatureModule],
            filesForUpload,
            checkedFormValues,
            isGalleryFilesUpload
          )
        : checkedFormValues;

    if (!formValuesForUpdate) {
      return;
    }

    // Process FormValues in case there's Gallery with updated fields
    const cleanFormValues = await getFullGalleryForUpdate(
      formValuesForUpdate,
      isGalleryFilesUpload,
      filesForUpload,
      uuid
    );

    APIUpdate(uuid, cleanFormValues)
      .then(() => {
        showNotification(
          "Success",
          "The update was successful.",
          EUiStates.SUCCESS
        );

        !callAfterUpdate &&
          history.push(
            generatePath(pathname.replace("modify", "detail"), { uuid: uuid })
          );
      })
      .catch((error) => {
        showNotification(
          "Error",
          `An error occurred while performing the update | ${error}`,
          EUiStates.DANGER
        );
      })
      .finally(() => {
        setIsUpdating(false);
        handleIsModalOpen();
        callAfterUpdate && callAfterUpdate();
        setIsProgressOpen(false);
        handleResetFileUpload();
      });
  };

  // Check if there are removeXXXUrl fields with true value and returns them
  const checkIfThereAreFilesForDelete = () => {
    const removeUrlsFields = Object.keys(formValues).filter(
      (key) =>
        key.toLowerCase().includes("remove") &&
        key.toLowerCase().includes("url")
    );

    const fieldsToRemove = removeUrlsFields.filter(
      (key: string) => formValues[key] === true
    );

    return fieldsToRemove;
  };

  const handleDeleteMultipleFiles = async (
    formValues: IFormValues,
    uuid: string
  ): Promise<IFormValues> => {
    for (const item of formValues.galleryFilesForDelete as ICreateMediaItem[]) {
      await deleteFileFromStorage(
        uuid,
        item.mediaUrl,
        signatureModulesMap[signatureModule]
      );
    }

    return formValues;
  };

  const handleDeleteSingleFile = async (
    formValues: IFormValues,
    uuid: string
  ): Promise<IFormValues> => {
    const fieldsToRemove = checkIfThereAreFilesForDelete();

    if (fieldsToRemove.length === 0) {
      return formValues;
    }

    fieldsToRemove.forEach(async (field) => {
      // It gets the field to remove from fieldsToRemoveMap (removeImageUrl -> imageUrl)
      const fieldToRemove =
        fieldsToRemoveMap[field as keyof typeof fieldsToRemoveMap];

      const previousUrl = await getPreviouslyUploadedUrl(uuid, fieldToRemove);

      await deleteFileFromStorage(
        uuid,
        previousUrl as string,
        signatureModulesMap[signatureModule]
      );

      formValues[fieldToRemove] = undefined;
      formValues[field] = false;
    });

    return formValues;
  };

  // Returns the previously uploaded url on the field to delete
  const getPreviouslyUploadedUrl = async (
    uuid: string,
    field: string
  ): Promise<string> => {
    // Gets the correct get service depending on module (partners or webinar for the moment)
    const getItemService =
      getPreviouslyUploadedUrlMap[
        signatureModule as keyof typeof getPreviouslyUploadedUrlMap
      ];

    // Gets the url from the requested field of the getted item
    const uploadedUrl = await getItemService(uuid).then(
      async (response: { data: any }) => {
        return response.data[field as keyof typeof response];
      }
    );

    return uploadedUrl;
  };

  return {
    isModalOpen,
    isUpdating,
    handleUpdate,
    handleIsModalOpen,
    handleSaveForm,
    totalFiles,
    totalSize,
    uploadedFiles,
    uploadedSize,
    isProgressOpen,
  };
};

export default useUpdateItem;
